/**
 * @license
 * @preserve
 *
 * MIT License
 * 
 * Copyright (c) 2022 KeeeX SAS
 * 
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 * 
 * ----------------------------------------
 * 
 * ISC License from bitcoinjs/bip39 project and source
 * 
 * Copyright (c) 2014, Wei Lu <luwei.here@gmail.com> and Daniel Cousens <email@dcousens.com>
 * 
 * Permission to use, copy, modify, and/or distribute this software for any
 * purpose with or without fee is hereby granted, provided that the above
 * copyright notice and this permission notice appear in all copies.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
 * WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
 * ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
 * WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
 * ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
 * OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 * 
 */
import { Algorithms as DigestAlgorithms } from "@keeex/crypto/lib/digesttypes.js";
import { bytesToBinary } from "./utils.js";
import { availableWordlists } from "./gen/wordlists.js";
import { checkEntropyLength, deriveChecksumBits, getSalt, MAX_ENTROPY_LENGTH, pbkdf2, randomBytes, } from "./crypto.js";
import { bitsToMnemonic, filterWordlist, mnemonicToBuf8, normalizeMnemonic } from "./mnemonic.js";
export { availableWordlists };
const BIP32_PBKDF2_ITERATIONS = 2048;
const BIP32_OUTPUTLEN = 64;
export const mnemonicToSeed = async (mnemonic, password, wordlistSource) => {
    const filteredMnemonic = await normalizeMnemonic(mnemonic, wordlistSource);
    const salt = getSalt(password);
    return pbkdf2(filteredMnemonic, salt, BIP32_PBKDF2_ITERATIONS, BIP32_OUTPUTLEN, DigestAlgorithms.SHA512);
};
export const mnemonicToEntropy = (mnemonic, wordlistSource) => mnemonicToBuf8(mnemonic, wordlistSource);
export const entropyToMnemonic = async (entropy, wordlistSource) => {
    checkEntropyLength(entropy);
    const entropyBits = bytesToBinary(Array.from(entropy));
    const checksumBits = await deriveChecksumBits(entropy);
    return bitsToMnemonic(entropyBits + checksumBits, wordlistSource);
};
/**
 * Generate a random BIP39 mnemonic.
 *
 * This allow retrieving a random seed and the associated mnemonic, to provide recovery the first
 * time the seed is used.
 */
export const generateRandom = async (password, wordlistSource) => {
    const entropy = await randomBytes(MAX_ENTROPY_LENGTH);
    const wordlist = await filterWordlist(wordlistSource);
    const mnemonic = await entropyToMnemonic(entropy, wordlist);
    const seed = await mnemonicToSeed(mnemonic, password, wordlist);
    return { entropy, wordlist, mnemonic, seed };
};
